import {
  ChannelConfigItem,
  ChipItem,
  DropdownOpt,
  ChipGroup,
  ListingCreateFormValues,
  ListingSearchFormValues,
} from "./types";

/*---------------------------------------------------------------
    INITIAL FORM VALUES
  ---------------------------------------------------------------*/

export const initialListingCreateForm: ListingCreateFormValues = {
  step: "select",
  search: "",
  vehicleManufacturerId: "",
  vehicleModelId: "",
  vehicleTypeId: "",
  selectedVehicle: null,
  baseTitle: "",
  basePrice: "",
  baseDescription: "",
};

export const initialListingSearchForm: ListingSearchFormValues = {
  name: null,
  status: null,
  salesChannelId: null,
};

/*---------------------------------------------------------------
    CHANNEL UI CONFIG
  ---------------------------------------------------------------*/

export const CHANNEL_CONFIG: Record<string, ChannelConfigItem> = {
  marketplace: {
    icon: "/media/icons/marketplace.svg",
    badge: { bg: "bg-violet-500", text: "text-white" },
    tab: { text: "text-violet-500", hover: "hover:text-violet-500", border: "border-b-violet-500" },
  },
  website: {
    icon: "/media/integrations/integration-default.svg",
    badge: { bg: "bg-purple-500", text: "text-white" },
    tab: { text: "text-purple-500", hover: "hover:text-purple-500", border: "border-b-purple-500" },
  },
  njuskalo: {
    icon: "/media/integrations/njuskalo.svg",
    badge: { bg: "bg-[#FDCD00]", text: "text-[#4b5563]" },
    tab: { text: "text-[#FDCD00]", hover: "hover:text-[#FDCD00]", border: "border-b-[#FDCD00]" },
  },
  index_oglasi: {
    icon: "/media/integrations/index.svg",
    badge: { bg: "bg-[#EB1E25]", text: "text-white" },
    tab: { text: "text-[#EB1E25]", hover: "hover:text-[#EB1E25]", border: "border-b-[#EB1E25]" },
  },
  plavi_oglasnik: {
    icon: "/media/integrations/plavi-oglasnik.svg",
    badge: { bg: "bg-[#039ED6]", text: "text-blue-100" },
    tab: { text: "text-[#039ED6]", hover: "hover:text-[#039ED6]", border: "border-b-[#039ED6]" },
  },
};

export const DEFAULT_CHANNEL_CONFIG: ChannelConfigItem = {
  icon: "/media/integrations/integration-default.svg",
  badge: { bg: "bg-gray-100", text: "text-gray-600" },
  tab: { text: "text-sky-600", hover: "hover:text-sky-600", border: "border-b-sky-500" },
};

export const CHANNEL_SORT_ORDER: Record<string, number> = {
  marketplace: 1,
  njuskalo: 2,
  plavi_oglasnik: 3,
  index_oglasi: 4,
  website: 5,
};

/*---------------------------------------------------------------
    MARKETPLACE CATALOG - EQUIPMENT
  ---------------------------------------------------------------*/

export const EQUIPMENT_ADDITIONAL: ChipItem[] = [
  { code: "aluminium_wheels", label: "Aluminijski naplaci" },
  { code: "sports_suspension", label: "Športsko podvozje" },
  { code: "4x4", label: "4x4" },
  { code: "third_brake_light", label: "3. stop svjetlo" },
  { code: "front_fog_lights", label: "Prednja svjetla za maglu" },
  { code: "tpms", label: "Nadzor pritiska u pneumaticima" },
  { code: "xenon_lights", label: "Ksenonska svjetla" },
  { code: "bi_xenon_lights", label: "Bi-ksenonska svjetla" },
  { code: "led_lights", label: "LED svjetla" },
  { code: "adaptive_lights", label: "Prilagodljiva svjetla" },
  { code: "light_sensor", label: "Senzor za svjetlo" },
  { code: "rain_sensor", label: "Senzor za kišu" },
  { code: "navigation", label: "Navigacija" },
  { code: "navigation_tv", label: "Navigacija + TV" },
  { code: "trip_computer", label: "Putno računalo" },
  { code: "cargo_net", label: "Mrežasta pregrada prtljažnika" },
  { code: "roof_rack", label: "Krovni nosači" },
  { code: "tow_hitch", label: "Kuka za vuču" },
  { code: "tinted_windows", label: "Zatamnjena stakla" },
  { code: "leather_steering_wheel", label: "Upravljač presvučen kožom" },
  { code: "sunroof", label: "Krovni prozor" },
  { code: "roof_box", label: "Krovna kutija" },
  { code: "head_up_display", label: "Head-up display" },
  { code: "start_stop", label: "Start-stop sistem" },
  { code: "disabled_adapted", label: "Prilagođeno za invalide" },
];

export const EQUIPMENT_SAFETY: ChipItem[] = [
  { code: "abs", label: "ABS" },
  { code: "esp", label: "ESP" },
  { code: "edc", label: "EDC" },
  { code: "ets", label: "ETS" },
  { code: "asr", label: "ASR" },
  { code: "asd", label: "ASD" },
  { code: "seatbelt_pretensioners", label: "Samozatezajući sigurnosni pojasevi" },
  { code: "isofix", label: "Isofix" },
  { code: "adaptive_cruise", label: "Adaptivni tempomat" },
  { code: "cruise_with_brake", label: "Tempomat s funkcijom kočenja" },
  { code: "lane_departure_warning", label: "Sustav upozorenja na napuštanje trake" },
  { code: "lane_keeping", label: "Zadržavanje u voznoj traci" },
  { code: "rear_collision_protection", label: "Zaštita od stražnjeg naleta" },
  { code: "side_collision_protection", label: "Zaštita od bočnog naleta" },
];

export const EQUIPMENT_ANTI_THEFT: ChipItem[] = [
  { code: "anti_theft_alarm", label: "Alarm" },
  { code: "engine_immobilizer", label: "Blokada motora" },
  { code: "central_locking", label: "Centralno zaključavanje" },
  { code: "central_locking_remote", label: "Centralno zaključavanje s daljinskim" },
  { code: "multilock", label: "Multilock" },
];

export const EQUIPMENT_COMFORT: ChipItem[] = [
  { code: "el_seat_adjustment", label: "El. podešavanje sjedala" },
  { code: "split_rear_bench", label: "Djeljiva stražnja klupa" },
  { code: "mobile_prep", label: "Priprema za mobilni aparat" },
  { code: "handsfree", label: "Handsfree" },
  { code: "el_mirror_adjustment", label: "El. podešavanje ogledala" },
  { code: "el_mirror_folding", label: "El. sklapanje ogledala" },
  { code: "cd_changer", label: "CD izmjenjivač" },
  { code: "el_front_windows", label: "El. podizanje prednjih stakala" },
  { code: "el_rear_windows", label: "El. podizanje stražnjih stakala" },
  { code: "height_adjustable_steering", label: "Upravljač podesiv po visini" },
  { code: "power_steering", label: "Servo upravljač" },
  { code: "heated_mirrors", label: "Grijanje ogledala" },
  { code: "parking_sensors", label: "Parkirni senzori" },
  { code: "front_camera", label: "Prednja parkirna kamera" },
  { code: "rear_camera", label: "Stražnja parkirna kamera" },
  { code: "cruise_control", label: "Tempomat" },
  { code: "central_armrest", label: "Središnji naslon za ruku" },
  { code: "leather_seats", label: "Kožna sjedala" },
  { code: "sport_seats", label: "Sportska sjedala" },
  { code: "heated_seats", label: "Grijanje sjedala" },
  { code: "steering_wheel_controls", label: "Komande na upravljaču" },
  { code: "android_auto", label: "Android Auto" },
  { code: "apple_carplay", label: "Apple CarPlay" },
];

export const EQUIPMENT_GROUPS: ChipGroup[] = [
  { title: "Dodatna oprema", items: EQUIPMENT_ADDITIONAL },
  { title: "Sigurnost", items: EQUIPMENT_SAFETY },
  { title: "Udobnost", items: EQUIPMENT_COMFORT },
];

/*---------------------------------------------------------------
    MARKETPLACE CATALOG - DROPDOWN OPTIONS
  ---------------------------------------------------------------*/

export const AIRBAGS_OPTIONS: DropdownOpt[] = [
  { value: "", text: "Nema" },
  { value: "2", text: "2" },
  { value: "4", text: "4" },
  { value: "6", text: "6" },
  { value: "8", text: "8" },
  { value: "10", text: "10+" },
];

export const AC_OPTIONS: DropdownOpt[] = [
  { value: "", text: "Nema" },
  { value: "manual", text: "Ručna" },
  { value: "auto", text: "Automatska" },
  { value: "dual", text: "Dvozonska" },
  { value: "tri", text: "Trozonska" },
  { value: "quad", text: "Četverozonska" },
];

export const RADIO_OPTIONS: DropdownOpt[] = [
  { value: "", text: "Nema" },
  { value: "basic", text: "Standardni" },
  { value: "premium", text: "Premium" },
  { value: "android_unit", text: "Android multimedija" },
];

export const FUEL_OPTIONS: DropdownOpt[] = [
  { value: "petrol", text: "Benzin" },
  { value: "diesel", text: "Dizel" },
  { value: "hybrid_petrol", text: "Hibrid - benzin" },
  { value: "hybrid_diesel", text: "Hibrid - dizel" },
  { value: "electric", text: "Električni" },
  { value: "lpg", text: "Plin (LPG)" },
  { value: "cng", text: "Plin (CNG)" },
  { value: "hydrogen", text: "Vodik" },
];

export const DRIVE_OPTIONS: DropdownOpt[] = [
  { value: "fwd", text: "Prednji" },
  { value: "rwd", text: "Stražnji" },
  { value: "awd", text: "4x4" },
];

export const TRANSMISSION_OPTIONS: DropdownOpt[] = [
  { value: "manual", text: "Ručni" },
  { value: "automatic", text: "Automatski" },
  { value: "semi", text: "Poluautomatski" },
];

export const BODY_OPTIONS: DropdownOpt[] = [
  { value: "limousine", text: "Limuzina" },
  { value: "hatchback", text: "Hatchback" },
  { value: "karavan", text: "Karavan" },
  { value: "suv", text: "SUV" },
  { value: "coupe", text: "Coupe" },
  { value: "cabrio", text: "Cabrio" },
  { value: "van", text: "Van" },
  { value: "pickup", text: "Pickup" },
];

export const CONDITION_OPTIONS: DropdownOpt[] = [
  { value: "new", text: "Novo" },
  { value: "used", text: "Rabljeno" },
  { value: "damaged", text: "Oštećeno" },
];

export const SUSPENSION_OPTIONS: DropdownOpt[] = [
  { value: "standard", text: "Standardno" },
  { value: "sport", text: "Sportsko" },
  { value: "air", text: "Zračno" },
];

export const ECO_CATEGORY_OPTIONS: DropdownOpt[] = [
  { value: "euro1", text: "Euro 1" },
  { value: "euro2", text: "Euro 2" },
  { value: "euro3", text: "Euro 3" },
  { value: "euro4", text: "Euro 4" },
  { value: "euro5", text: "Euro 5" },
  { value: "euro6", text: "Euro 6" },
  { value: "euro6d", text: "Euro 6d" },
];

export const WARRANTY_OPTIONS: DropdownOpt[] = [
  { value: "none", text: "Bez jamstva" },
  { value: "12m", text: "12 mjeseci" },
  { value: "24m", text: "24 mjeseca" },
  { value: "factory", text: "Tvorničko jamstvo" },
];

export const OWNER_COUNT_OPTIONS: DropdownOpt[] = [
  { value: "1", text: "Prvi vlasnik" },
  { value: "2", text: "Drugi vlasnik" },
  { value: "3", text: "Treći vlasnik" },
  { value: "4+", text: "Četvrti i više" },
];

export const REGISTERED_UNTIL_OPTIONS: DropdownOpt[] = [
  { value: "none", text: "Nije registrirano" },
  { value: "1-3", text: "1 – 3 mjeseca" },
  { value: "3-6", text: "3 – 6 mjeseci" },
  { value: "6-12", text: "6 – 12 mjeseci" },
];

export const PAYMENT_OPTIONS: ChipItem[] = [
  { code: "cash", label: "Gotovina" },
  { code: "credit", label: "Kredit" },
  { code: "leasing", label: "Leasing" },
  { code: "leasing_takeover", label: "Preuzimanje leasinga" },
  { code: "card_installments", label: "Obročno bankovnim karticama" },
  { code: "trade_in", label: "Staro za novo" },
  { code: "exchange", label: "Zamjena" },
];

export const TITLE_MAX_LENGTH = 70;
export const DESCRIPTION_MAX_LENGTH = 4000;
